/**
 * The seam between the imperative deck and the reactive UI.
 *
 * Deck events are pushed into signals here, and nowhere else — which is why
 * no component ever has to poll the transport.
 */

import { updateTweens } from '../lib/tween.ts';
import * as player from '../state/player.ts';
import { toast } from '../state/ui.ts';
import { hardware } from './hardware.ts';
import { skip } from './playback.ts';

/** How often the level meter and its ring are refreshed, in milliseconds. */
const METER_MS = 66;

export function wireDeck(): void {
  const { deck } = player;

  deck.on('timeupdate', (pos, dur) => {
    const { tt } = hardware();
    player.setPosition(pos);
    player.setDuration(dur);
    if (dur) tt.setProgress(pos / dur);
  });

  deck.on('durationchange', (d) => {
    player.setDuration(d);
  });

  deck.on('play', () => {
    player.setPlaying(true);
    hardware().tt.setSpinning(true);
  });

  deck.on('pause', () => {
    player.setPlaying(false);
  });

  deck.on('error', (msg) => {
    toast(msg);
    player.setPlaying(false);
  });

  deck.on('wantnext', () => {
    void skip(1, { auto: true });
  });

  deck.on('volume', (v, m) => {
    player.setVolume(v);
    player.setMuted(m);
  });

  deck.on('queue', (queue) => {
    player.setQueue(queue);
  });
}

export interface Loop {
  stop: () => void;
}

/**
 * The render loop. One clock drives the tween engine, the deck's mechanics and
 * the renderer, so animation, physics and the camera never disagree about time.
 */
export function startRenderLoop(): Loop {
  const { stage, tt } = hardware();
  let last = performance.now();
  let lastMeter = 0;
  let frame = 0;
  let running = true;

  const step = (): void => {
    if (!running) return;
    frame = requestAnimationFrame(step);
    const now = performance.now();
    const dt = Math.min((now - last) / 1000, 0.1);
    last = now;

    updateTweens(now);
    tt.update(dt);
    stage.render();

    /* The level meter runs at a calmer rate than the frame loop. */
    if (now - lastMeter > METER_MS) {
      lastMeter = now;
      const lv = player.deck.level;
      tt.setLevel(lv);
      player.setLevel(lv);
    }
  };

  frame = requestAnimationFrame(step);
  return {
    stop() {
      running = false;
      cancelAnimationFrame(frame);
    },
  };
}

/** Pause when the tab is hidden, so a phone does not keep the platter spinning. */
export function wireVisibility(): () => void {
  const onHidden = (): void => {
    if (!document.hidden || !player.deck.playing || player.deck.source !== 'demo') return;
    player.deck.pause();
    hardware().tt.setSpinning(false);
  };
  document.addEventListener('visibilitychange', onHidden);
  return () => {
    document.removeEventListener('visibilitychange', onHidden);
  };
}
